/**
 * The Accounting section's reads (the bookkeeping ledger, plans 01-05).
 *
 * Same rules as every other query module: URLs are declared here where
 * `api-requests.test.ts` can execute them against the Worker catalog, never
 * inside a `useQuery` closure.
 *
 * The review queue is a LIVE_LIST — it is the queue an operator is working
 * through right now, and a stale one means reviewing something already decided.
 * The reports are AGGREGATE: each is a recursive CTE over every journal line in
 * the book, nobody expects them second-fresh, and a screen full of them
 * re-running because a tab regained focus is pure cost against a number that
 * did not move.
 */
import { apiGet } from "@/lib/api";
import { keepWhileScope } from "@/lib/list-placeholder";
import { AGGREGATE, DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import { expandEventKinds } from "@/types-accounting-reports";
import type {
  BalanceDifferenceResponse,
  BalanceSheetResponse,
  ContactsResponse,
  EventsResponse,
  FeedConnectionsResponse,
  FeedLinesResponse,
  ForeignBalancesResponse,
  ImportBatchDetail,
  ImportsResponse,
  LedgerAccountsResponse,
  LedgerBooksResponse,
  ProfitAndLossResponse,
  ReportResult,
  ReviewQueueResponse,
  TemplateDetailResponse,
  TemplatesResponse,
  TransfersResponse,
} from "@/types-accounting";

/**
 * Review groups per read: the API's maximum, a whole book's backlog, so the
 * list shows every vendor waiting. It says "Showing X of Y" when there are more.
 */
const REVIEW_LIMIT = 500;

export function ledgerBooksQuery() {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-books"] as const,
    queryFn: () => apiGet<LedgerBooksResponse>("/api/admin/bookkeeping/books"),
  };
}

/**
 * One book's row of the Books list (`?bookId=`): the open book's counts after a
 * decision, without recounting the fleet. Same response shape as the list.
 */
export function ledgerBookQuery(bookId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-book", bookId] as const,
    queryFn: () =>
      apiGet<LedgerBooksResponse>("/api/admin/bookkeeping/books", { bookId }),
  };
}

/**
 * One company's review queue. The book is required: the unscoped queue mixed
 * every customer's samples, and the screen reviews one company at a time.
 * Every key starts `["accounting-review"]`, so a decision invalidates all of
 * them by prefix.
 */
export function reviewQueueQuery({
  bookId,
  q,
  limit = REVIEW_LIMIT,
}: {
  bookId: string;
  /** Search; the API keeps every vendor with a matching line. */
  q?: string;
  limit?: number;
}) {
  const search = (q ?? "").trim();
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-review", bookId, limit, search] as const,
    // A new search keeps the book's list on screen (dimmed) until it answers.
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: () =>
      apiGet<ReviewQueueResponse>("/api/admin/bookkeeping/review", {
        limit,
        bookId,
        q: search,
      }),
  };
}

export function feedConnectionsQuery() {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-connections"] as const,
    queryFn: () =>
      apiGet<FeedConnectionsResponse>("/api/admin/feed/connections"),
  };
}

/**
 * One book's chart of accounts — the names behind the review queue's codes.
 *
 * AGGREGATE rather than LIVE_LIST: a chart changes when an admin adds an
 * account or a customer links a bank, which is not the timescale a reviewer
 * works at, and a queue holding several open groups would otherwise refetch the
 * same ~75 rows every time the tab regained focus. React Query dedupes by key,
 * so every sample sharing a book costs one request between them.
 */
export function ledgerAccountsQuery(bookId: string) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-accounts", bookId] as const,
    queryFn: () =>
      apiGet<LedgerAccountsResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/accounts`,
      ),
  };
}

export interface ProfitAndLossParams {
  bookId: string;
  from: string;
  to: string;
  /** 'cash' or 'full'. Upstream defaults an admin read to the full ledger. */
  basis: string;
}

export function profitAndLossQuery(params: ProfitAndLossParams) {
  return {
    ...AGGREGATE,
    queryKey: [
      "accounting-pl",
      params.bookId,
      params.from,
      params.to,
      params.basis,
    ] as const,
    queryFn: () =>
      apiGet<ProfitAndLossResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(params.bookId)}/reports/profit-and-loss`,
        { from: params.from, to: params.to, basis: params.basis },
      ),
  };
}

export function balanceSheetQuery(bookId: string, asAt: string) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-balance-sheet", bookId, asAt] as const,
    queryFn: () =>
      apiGet<BalanceSheetResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/balance-sheet`,
        { asAt },
      ),
  };
}

export function foreignBalancesQuery(bookId: string, asAt: string) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-foreign-balances", bookId, asAt] as const,
    // A new period keeps the last disclosure on screen until its own arrives.
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: () =>
      apiGet<ForeignBalancesResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/foreign-balances`,
        { asAt },
      ),
  };
}

export function balanceDifferenceQuery(bookId: string) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-balance-difference", bookId] as const,
    queryFn: () =>
      apiGet<BalanceDifferenceResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/balance-difference`,
      ),
  };
}

/**
 * The book's transfer pairs.
 *
 * LIVE_LIST: a proposal is a decision waiting on a human, and it disappears the
 * moment someone makes it — the same freshness the reconcile list needs.
 */
export function bookTransfersQuery(bookId: string, state?: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-transfers", bookId, state ?? ""] as const,
    queryFn: () =>
      apiGet<TransfersResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/transfers`,
        state ? { state } : undefined,
      ),
  };
}

export interface BookLinesParams {
  bookId: string;
  feedAccountId?: string;
  status?: string;
  reconciliation?: string;
  source?: string;
  /** "0" or "1"; anything else is dropped by the Worker's allowlist anyway. */
  pending?: string;
  from?: string;
  to?: string;
  amountMin?: number;
  amountMax?: number;
  /** "1": the bounds compare the size of the amount, not its signed value. */
  amountAbs?: string;
  q?: string;
  /** A chart account code: only lines whose posted entry hits that account. */
  category?: string;
  includeExcluded?: boolean;
  cursor?: string;
  limit?: number;
}

/**
 * A book's feed lines — the read behind reconcile, cash coding and statements.
 *
 * LIVE_LIST, not AGGREGATE: this is the queue an operator is working through
 * right now, and a stale page means coding something already coded. The key
 * carries every filter, so two tabs with different filters never share an
 * answer.
 */
export function bookLinesQuery(params: BookLinesParams) {
  const { bookId, includeExcluded, ...rest } = params;
  const query = {
    ...rest,
    ...(includeExcluded ? { includeExcluded: "1" } : {}),
  };
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-lines", bookId, query] as const,
    // A bank account, filter or page switch keeps the last lines on screen
    // (dimmed) until the new ones arrive; another book never lends its lines.
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: () =>
      apiGet<FeedLinesResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/lines`,
        query,
      ),
  };
}

/**
 * The candidate counter-legs for one line.
 *
 * The rules that make a line a candidate live with the detector
 * (`fx/detect-transfer.ts`), so the browser names the line and reads the
 * answer rather than re-deriving equal-magnitude, opposite-sign, other-account
 * and the date window for itself.
 */
export function counterLegQuery(bookId: string, feedLineId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-counter-leg", bookId, feedLineId] as const,
    queryFn: () =>
      apiGet<FeedLinesResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/lines`,
        { counterLegFor: feedLineId },
      ),
  };
}

/**
 * The counter-legs for one leg of a PROPOSED transfer, with that proposal
 * treated as released: "Change match" asks for a new partner for the leg it
 * keeps. The API leaves out the proposal's current partner.
 */
export function counterLegExceptQuery(
  bookId: string,
  feedLineId: string,
  exceptTransferId: string,
) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "accounting-counter-leg",
      bookId,
      feedLineId,
      "except",
      exceptTransferId,
    ] as const,
    queryFn: () =>
      apiGet<FeedLinesResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/lines`,
        { counterLegFor: feedLineId, exceptTransferId },
      ),
  };
}

/**
 * One line by id, for the drawer's `?line=` deep link when that line is not on
 * the page being shown. The API ignores every other filter beside `id` and
 * answers `[]` for another book's line. Keyed under `accounting-lines`, so the
 * refresh after a write re-reads it with the list.
 */
export function bookLineQuery(bookId: string, feedLineId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-lines", bookId, { id: feedLineId }] as const,
    queryFn: () =>
      apiGet<FeedLinesResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/lines`,
        { id: feedLineId },
      ),
  };
}

/** The contact picker's options. AGGREGATE: a contact list is not a queue. */
export function bookContactsQuery(bookId: string, kind?: string) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-contacts", bookId, kind ?? ""] as const,
    queryFn: () =>
      apiGet<ContactsResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/contacts`,
        kind ? { kind } : undefined,
      ),
  };
}

export function bookEventsQuery(
  bookId: string,
  opts: {
    limit?: number;
    kinds?: string;
    actor?: string;
    /** person | rule | ai | system | customer (round 3, R8). */
    actorKind?: string;
    cursor?: string;
    from?: string;
    to?: string;
    subjectType?: string;
    subjectId?: string;
  } = {},
) {
  return {
    ...LIVE_LIST,
    // The key holds what the SCREEN chose; the request holds what the API
    // understands. Keying on the expansion would change the cache key whenever
    // a prefix gains a kind, for a filter the operator never touched.
    queryKey: ["accounting-events", bookId, opts] as const,
    queryFn: () =>
      apiGet<EventsResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/events`,
        {
          ...opts,
          // `EVENT_KIND_FILTERS` offers a prefix; `listEvents` matches
          // `kind IN (…)` exactly and has no prefix clause.
          ...(opts.kinds ? { kinds: expandEventKinds(opts.kinds) } : {}),
          // `created_at` is an ISO timestamp, so `created_at <= '2026-03-31'`
          // is false for every event on 31 March. A date control that hides
          // the day it names is worse than no date control.
          ...(opts.to ? { to: endOfDay(opts.to) } : {}),
        },
      ),
  };
}

/** A bare `YYYY-MM-DD` as the last instant of that day. Anything else passes. */
function endOfDay(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T23:59:59.999Z` : value;
}

export function importBatchesQuery(bookId: string, status?: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-imports", bookId, status ?? ""] as const,
    queryFn: () =>
      apiGet<ImportsResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/imports`,
        status ? { status } : undefined,
      ),
  };
}

export function importBatchQuery(bookId: string, batchId: string) {
  return {
    ...DETAIL,
    queryKey: ["accounting-import", bookId, batchId] as const,
    queryFn: () =>
      apiGet<ImportBatchDetail>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/imports/${encodeURIComponent(batchId)}`,
      ),
  };
}

export interface LedgerReportParams {
  bookId: string;
  accountId: string;
  from: string;
  to: string;
  basis: string;
  cursor?: string;
  limit?: number;
}

/** The general ledger for one account: lines in date order with a running balance. */
export function generalLedgerQuery(params: LedgerReportParams) {
  const { bookId, ...query } = params;
  return {
    ...AGGREGATE,
    queryKey: ["accounting-general-ledger", bookId, query] as const,
    // Switching account, year or basis keeps the last ledger (dimmed).
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: () =>
      apiGet<ReportResult>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/general_ledger`,
        query,
      ),
  };
}

/** The same shape for the drill-down the customer app already reads. */
export function accountTransactionsQuery(params: LedgerReportParams) {
  const { bookId, ...query } = params;
  return {
    ...AGGREGATE,
    queryKey: ["accounting-account-transactions", bookId, query] as const,
    queryFn: () =>
      apiGet<ReportResult>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/account_transactions`,
        query,
      ),
  };
}

/**
 * The shipped charts of accounts (plan 18).
 *
 * AGGREGATE rather than LIVE_LIST, and the reason is stronger here than
 * anywhere else in this file: these rows live in ProductData's SOURCE. They
 * change when a deploy changes them and at no other moment, so a screenful of
 * them refetching because a tab regained focus is pure cost against a number
 * that cannot have moved.
 */
export function templatesQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["chart-templates"] as const,
    queryFn: () =>
      apiGet<TemplatesResponse>("/api/admin/bookkeeping/templates"),
  };
}

export function templateQuery(templateId: string) {
  return {
    ...AGGREGATE,
    queryKey: ["chart-template", templateId] as const,
    queryFn: () =>
      apiGet<TemplateDetailResponse>(
        `/api/admin/bookkeeping/templates/${encodeURIComponent(templateId)}`,
      ),
  };
}

// The report engine, year-close and event reads (plans 11, 12) live in their
// own module for the same reason every other block does, and are re-exported
// so `@/lib/queries` stays the one import a screen or a test reaches for.
export * from "./queries-accounting-reports";
